import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./DocumentsTable.css";
import UploadModal from "./upload";
import { useUser } from "../../context/UserContext";
import { routeBasedOnSubscription } from "../../services/commonService";
import { listDocuments, deleteDocument } from "../../services/accIqService";

/* ---------- COMMENTED-OUT DUMMY DATA ---------- */
// const initialData = Array.from({ length: 24 }, (_, i) => ({
//   id: i + 1,
//   name: `Document ${i + 1}`,
//   type: "PDF",
//   date: "2024-02-10"
// }));

export default function DocumentsTable() {
  const navigate = useNavigate();
  const { user, handleLogout } = useUser();

  const [documents, setDocuments] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null); // holds document_id being deleted
  const [showUpload, setShowUpload] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // holds doc pending confirmation
  const [totalDocs, setTotalDocs] = useState(0); // total document count from API

  const rowsPerPage = 5;

  const userId = user?._id || user?.id || "";
  const client = user?.company_name_for_growth_iq || user?.company_name || "";

  /* ---------- fetch documents ---------- */

  const fetchDocs = useCallback(async (targetPage) => {
    if (!userId || !client) return;
    setLoading(true);
    try {
      const data = await listDocuments(userId, client, targetPage, rowsPerPage);
      setDocuments(data.results || []);
      setTotalPages(data.total_pages || 1);
      setTotalDocs(data.total || 0);
      setPage(data.page || targetPage);
    } catch (err) {
      console.error("Failed to fetch documents", err);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [userId, client, rowsPerPage]);

  useEffect(() => {
    fetchDocs(page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- delete ---------- */

  const handleDeleteClick = (doc) => {
    setConfirmDelete(doc);
  };

  const handleConfirmYes = async () => {
    if (!confirmDelete || deleting) return;
    const documentId = confirmDelete.document_id;
    setConfirmDelete(null);
    setDeleting(documentId);
    try {
      await deleteDocument(documentId);

      // If only one row on this page and we're not on page 1, go back one page
      if (documents.length === 1 && page > 1) {
        setPage((p) => p - 1);
      } else {
        await fetchDocs(page);
      }
    } catch (err) {
      console.error("Failed to delete document", err);
    } finally {
      setDeleting(null);
    }
  };

  const handleConfirmNo = () => {
    setConfirmDelete(null);
  };

  /* ---------- upload success callback ---------- */

  const handleUploadSuccess = () => {
    // Refresh page 1 so the newly uploaded doc is visible
    setPage(1);
    fetchDocs(1);
  };

  /* ---------- navigation ---------- */

  const handleCancel = () => {
    const routePath = routeBasedOnSubscription(user, handleLogout);
    if (routePath) {
      navigate(routePath, { replace: true });
    }
  };

  /* ---------- format date ---------- */

  const formatDate = (isoStr) => {
    if (!isoStr) return "—";
    const d = new Date(isoStr);
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  /* ---------- pagination helpers ---------- */

  const getVisiblePages = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    // always show first page
    pages.push(1);
    if (page > 3) pages.push("...");
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    if (page < totalPages - 2) pages.push("...");
    // always show last page
    pages.push(totalPages);
    return pages;
  };

  /* ---------- render ---------- */

  const currentRows = documents;
  const startEntry = currentRows.length > 0 ? (page - 1) * rowsPerPage + 1 : 0;
  const endEntry = (page - 1) * rowsPerPage + currentRows.length;

  return (
    <div className="profile-wrapper">

      {/* RIGHT CARD */}

      <div className="profile-card">

        {/* TITLE ROW */}
        <div className="doc-title-row">
          <h2 className="doc-header-title">Document Memory</h2>
          <div className="card-close" onClick={handleCancel}>✕</div>
        </div>

        {/* NOTE */}
        <div className="doc-note">
          <span className="doc-note-label">Note: </span>
          <strong>Upload documents that contain information you want the AI to remember.</strong><br />
          These files will be used as private knowledge when answering your questions,
          performing deep research, or generating Company IQ insights.
        </div>

        {/* UPLOAD BUTTON */}
        <div className="doc-upload-row">
          <button className="upload-btn" onClick={() => setShowUpload(true)}>
            Upload
          </button>
        </div>

        {/* TABLE */}

        <div className="table-area">

          <table className="doc-table">

            <thead>
              <tr>
                <th>#</th>
                <th>Document</th>
                <th>Type</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>

              {loading ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: "20px" }}>
                    Loading…
                  </td>
                </tr>
              ) : currentRows.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: "20px", color: "#64748B" }}>
                    No documents found.
                  </td>
                </tr>
              ) : (
                currentRows.map((doc, index) => (
                  <tr key={doc.document_id}>
                    <td>{(page - 1) * rowsPerPage + index + 1}</td>
                    <td>{doc.filename}</td>
                    <td>{doc.document_type}</td>
                    <td>{formatDate(doc.created_at)}</td>

                    <td>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteClick(doc)}
                        disabled={deleting === doc.document_id}
                      >
                        {deleting === doc.document_id ? "Deleting…" : "Delete"}
                      </button>
                    </td>

                  </tr>
                ))
              )}

              {/* empty rows to maintain height */}

              {!loading &&
                Array.from({
                  length: rowsPerPage - currentRows.length
                }).map((_, i) => (
                  <tr key={"empty" + i} className="empty-row">
                    <td colSpan="5"></td>
                  </tr>
                ))}

            </tbody>

          </table>

        </div>

        {/* PAGINATION */}

        <div className="pagination">

          <span className="pagination-info">
            Showing {startEntry} to {endEntry} of {totalDocs} entries
          </span>

          <div className="pagination-controls">
            <button
              className="page-btn page-nav"
              disabled={page === 1 || loading}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>

            {getVisiblePages().map((p, i) =>
              p === "..." ? (
                <span key={"dots" + i} className="page-dots">…</span>
              ) : (
                <button
                  key={p}
                  className={`page-btn ${p === page ? "page-active" : ""}`}
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              )
            )}

            <button
              className="page-btn page-nav"
              disabled={page === totalPages || loading}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>

        </div>

        {/* FOOTER */}

        {/* <div className="card-footer">

          <button className="cancel-btn" onClick={handleCancel}>
            Cancel
          </button>

          <button className="signout-btn" onClick={handleLogout}>
            Sign out
          </button>

        </div> */}

      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {confirmDelete && (
        <div className="confirm-overlay">
          <div className="confirm-box">
            <p>Are you sure you want to delete <strong>{confirmDelete.filename}</strong>?</p>
            <div className="confirm-actions">
              <button className="confirm-yes" onClick={handleConfirmYes}>Yes</button>
              <button className="confirm-no" onClick={handleConfirmNo}>No</button>
            </div>
          </div>
        </div>
      )}

      {/* UPLOAD MODAL */}
      <UploadModal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        onUploadSuccess={handleUploadSuccess}
      />

    </div>
  );
}